import { Timer } from '../../game/model/timer';
import { BehaviorSubject, Subscription } from 'rxjs';
import { t } from '../../../utils/helpers';
import { ModalButton, OpponentIdleActionsDialog } from './interfaces';
import { ITable } from '@base/features/game/model/interfaces';
import { IPlayersOnlineRepository } from '@base/features/player/model/interfaces';
import Codes from '../../socket/codes';
import Utils from '../../socket/utils';
import LobbyService from '@base/features/lobby/model/lobbyService';
import { ISocketClient } from '@base/features/socket/interfaces';

export default class OpponentIdleActionsDialogImpl implements OpponentIdleActionsDialog {
  title = new BehaviorSubject<string>('');
  message = new BehaviorSubject<string>('');
  buttons = new BehaviorSubject<ModalButton[]>([]);
  visible = new BehaviorSubject<boolean>(false);

  private reconnectionTimer: Timer | undefined;
  private realPlayersCount = 0;
  private showControls = true;
  private playMode = false;
  private timerTickSubscription: Subscription | undefined;
  private timerEndSubscription: Subscription | undefined;
  private playerListChangedSubscription: Subscription | undefined;

  constructor(
    private table: ITable,
    private playersOnlineRepository: IPlayersOnlineRepository,
    private lobbyService: LobbyService,
    private RECONNECTION_TIME: number,
    private socket: ISocketClient,
  ) {}

  private onPlayersChanged() {
    this.realPlayersCount = this.table.getCountOfPlayers(true);
    this.updateState();
  }

  private updateButtons() {
    this.buttons.next(
      this.realPlayersCount === 2 && this.showControls
        ? [
            { title: t('abort'), onClick: this.handlerAbortClick.bind(this) },
            {
              title: t('take_win'),
              onClick: this.handlerTakeWinClick.bind(this),
            },
            {
              title: t('wait_again'),
              onClick: this.handlerWaitAgainClick.bind(this),
            },
          ]
        : [{ title: t('close'), onClick: () => this.visible.next(false) }]
    );
  }

  private updateTitle() {
    const title =
      this.reconnectionTimer?.formatTime(this.reconnectionTimer.limit - this.reconnectionTimer.elapsed) || '';
    this.title.next(title);
  }

  private updateMessage() {
    let message: string;
    if (this.realPlayersCount > 2) {
      message = t('opponent_lost_connection_please_wait');
    } else if (this.showControls) {
      message = t('opponent_time_expired');
    } else {
      message = t('opponent_lost_connection_please_wait');
    }
    this.message.next(message);
  }

  private updateState() {
    this.updateTitle();
    this.updateMessage();
    this.updateButtons();
  }

  private handlerTimerTick() {
    if (!this.visible.value) return;
    this.updateState();
  }

  private handlerTimerEnd() {
    if (!this.visible.value) return;
    this.showControls = true;
    this.updateState();
  }

  private handlerAbortClick() {
    const me = this.playersOnlineRepository.me!;
    const table = me!.getTable()!;

    const mode = this.playMode ? Codes.TableOpMode.ABORT_PM : Codes.TableOpMode.ABORT_R;
    this.lobbyService.sendTableOp(me.id!, table.tid, mode);
    this.showControls = false;
    this.updateState();
  }

  private handlerTakeWinClick() {
    const me = this.playersOnlineRepository.me!;
    const table = me.getTable()!;

    const mode = this.playMode ? Codes.TableOpMode.WIN_PM : Codes.TableOpMode.WIN_R;
    this.lobbyService.sendTableOp(me.id!, table.tid, mode);
    this.showControls = false;
    this.updateState();
  }

  private handlerWaitAgainClick() {
    const me = this.playersOnlineRepository.me!;
    const table = me!.getTable()!;

    const type = this.playMode ? Codes.ServiceTypes.PM_WAIT : Codes.ServiceTypes.R_WAIT;
    this.sendService(type, 0, table.tid!);
    this.showControls = false;
    this.updateState();
  }

  sendService(type: any, pid: number, tid: number): void {
    const data = [Codes.Types.CP_SERVICE]
      .concat(Utils.decToInt8(type))
      .concat(Utils.decToInt16(tid))
      .concat(Utils.decToInt16(pid));
    this.socket.sendPacket(data); 
  }

  show(playmode: boolean, showControls: boolean) {
    this.playMode = playmode;
    this.showControls = showControls;
    this.realPlayersCount = this.table.getCountOfPlayers(true);

    this.reconnectionTimer = new Timer(this.RECONNECTION_TIME, 0);
    this.timerTickSubscription = this.reconnectionTimer.onTick.subscribe(this.handlerTimerTick.bind(this));
    this.timerEndSubscription = this.reconnectionTimer.onEnd.subscribe(this.handlerTimerEnd.bind(this));
    this.reconnectionTimer.start();

    this.playerListChangedSubscription = this.playersOnlineRepository.onPlayersListChanged.subscribe(
      this.onPlayersChanged.bind(this)
    );

    this.visible.next(true);
    this.updateState();
  }

  hide() {
    this.visible.next(false);
    const timer = this.reconnectionTimer;
    if (timer) {
      this.timerTickSubscription?.unsubscribe();
      this.timerEndSubscription?.unsubscribe();
      timer.stop();
      this.reconnectionTimer = undefined;
    }

    this.playerListChangedSubscription?.unsubscribe();
  }

  setTime(elapsedTime: number) {
    if (this.reconnectionTimer) {
      this.reconnectionTimer.elapsed = elapsedTime;
    }
  }

  end() {
    this.reconnectionTimer?.end();
  }

  wait() {
    this?.reconnectionTimer?.restart();
  }
}
